import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_BASE_URL, BaseUrls } from '@app/app.constants';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { QueueWithUser } from '@app/core/interfaces/c5/acd/queue-with-user';
import { RouteConfig } from '@app/core/interfaces/c5/acd/route-config';
import { Utils } from '@app/shared/services/utils.service';
import { wrapError } from '@app/utils/rxjs';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { QueueAgent } from '../interfaces/c5/acd/queue-agent';
import { UserLevel } from '../models/user-level';

@Injectable({
  providedIn: 'root',
})
export class AcdRepository {
  constructor(
    private http: HttpClient,
    private utils: Utils
  ) {}

  add(acd: Partial<Queue>): Observable<void> {
    return this.utils.apiUrl(`/acdQueue`).pipe(
      mergeMap((url) => this.http.post<void>(url, acd)),
      wrapError('acd-repository:add')
    );
  }

  delete(id: number): Observable<void> {
    return this.utils.apiUrl(`/acdQueue/${id}`).pipe(
      mergeMap((url) => this.http.delete<void>(url)),
      wrapError('acd-repository:delete')
    );
  }

  get(id: number): Observable<QueueWithUser> {
    return this.utils.apiUrl(`/acdQueue/${id}`).pipe(
      mergeMap((url) => this.http.get<QueueWithUser>(url)),
      wrapError('acd-repository:get')
    );
  }

  list(enterpriseId?: number | string): Observable<Queue[]> {
    return this.utils
      .apiUrl(
        {
          [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.Enterprise}/acdQueue`,
          [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.Enterprise}/acdQueue`,
        },
        { enterpriseId }
      )
      .pipe(
        mergeMap((url) => this.http.get<Queue[]>(url)),
        wrapError('acd-repository:list')
      );
  }

  /**
   * List acd and call center queues for an enterprise.
   */
  listAllQueues(enterpriseId: number | string): Observable<Queue[]> {
    const url: string = `${API_BASE_URL}/v1/enterprise/${enterpriseId}/allAcdQueues`;

    return this.http.get<Queue[]>(url).pipe(wrapError('acd-repository:list-all-enterprise-acd-queues'));
  }

  listAllEndUserAgents(): Observable<QueueAgent[]> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.EndUser}/agents`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.EndUser}/agents`,
        [UserLevel.EndUser]: `/user/agents`,
      })
      .pipe(
        mergeMap((url) => this.http.get<QueueAgent[]>(url)),
        wrapError('acd-repository:end-user-all-queue-agents')
      );
  }

  listRouteConfigs(queueId: number): Observable<RouteConfig[]> {
    return this.utils
      .apiUrl({
        [UserLevel.ServiceProviderAdmin]: `${BaseUrls.ServiceProvider.Enterprise}/acdQueue/${queueId}/routeConfigs`,
        [UserLevel.EnterpriseAdmin]: `${BaseUrls.Enterprise.Enterprise}/acdQueue/${queueId}/routeConfigs`,
        [UserLevel.EndUser]: `/user/acd/queue/${queueId}/routeConfigs`,
      })
      .pipe(
        mergeMap((url) => this.http.get<RouteConfig[]>(url)),
        wrapError('acd-repository:list-route-config')
      );
  }

  update(acd: Queue): Observable<void> {
    return this.utils.apiUrl(`/acdQueue/${acd.id}`).pipe(
      mergeMap((url) => this.http.put<void>(url, acd)),
      wrapError('acd-repository:update')
    );
  }
}
